/**
 * Google Maps Place Photos Service (Browser-Compatible Version)
 * 
 * This service fetches scenic photographs for state parks using Google Maps JavaScript API.
 * It uses the Places Service which is CORS-friendly for browser applications.
 * 
 * IMPORTANT: This requires a Google Maps API key with Places API enabled.
 * Set VITE_GOOGLE_MAPS_API_KEY in your .env file.
 */

const GOOGLE_MAPS_API_KEY = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;

// Google Maps loader promise to ensure script is loaded only once
let googleMapsPromise = null;

/**
 * Validate if Google Maps API key is properly configured
 * @returns {boolean}
 */
const isValidApiKey = () => {
  // Check if key exists and is not the placeholder value
  if (!GOOGLE_MAPS_API_KEY || 
      GOOGLE_MAPS_API_KEY === 'your-google-maps-api-key-here' ||
      GOOGLE_MAPS_API_KEY?.trim() === '') {
    return false;
  }
  return true;
};

/**
 * Load Google Maps JavaScript API dynamically
 * @returns {Promise<void>}
 */
const loadGoogleMapsScript = () => {
  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    // Validate API key before attempting to load
    if (!isValidApiKey()) {
      const errorMsg = 'Google Maps API key not configured or invalid. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file with a valid API key from https://console.cloud.google.com/google/maps-apis/';
      console.warn('⚠️', errorMsg);
      reject(new Error(errorMsg));
      return;
    }

    // Create script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.google && window.google.maps) {
        console.log('✅ Google Maps API loaded successfully');
        resolve();
      } else {
        reject(new Error('Google Maps API failed to load'));
      }
    };

    script.onerror = (error) => {
      const errorMsg = 'Failed to load Google Maps API script. Please check your API key and ensure Maps JavaScript API and Places API are enabled in Google Cloud Console.';
      console.error('❌', errorMsg, error);
      reject(new Error(errorMsg));
    };

    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

/**
 * Search for a place using Places Service (browser-compatible)
 * @param {string} query - The search query (e.g., "Yellowstone State Park")
 * @param {Object} location - Optional location bias {lat, lng}
 * @returns {Promise<Object>} Place details with place_id
 */
export const searchPlace = async (query, location = null) => {
  try {
    if (!isValidApiKey()) {
      console.warn('⚠️ Google Maps API key not configured. Using fallback images. Configure VITE_GOOGLE_MAPS_API_KEY in .env file.');
      return null;
    }

    // Load Google Maps API
    await loadGoogleMapsScript();

    // Create a temporary map element (required for PlacesService)
    const mapDiv = document.createElement('div');
    const map = new window.google.maps.Map(mapDiv);

    // Create PlacesService instance
    const service = new window.google.maps.places.PlacesService(map);

    // Create request
    const request = {
      query: query,
      fields: ['place_id', 'name', 'formatted_address', 'geometry', 'photos']
    };

    // Add location bias if provided
    if (location) {
      request.locationBias = {
        center: new window.google.maps.LatLng(location.lat, location.lng),
        radius: 50000 // 50km radius
      };
    }

    return new Promise((resolve, reject) => {
      service.findPlaceFromQuery(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          const place = results[0];
          console.log('✅ Found place:', place.name, 'with', place.photos?.length || 0, 'photos');
          resolve(place);
        } else {
          console.warn('⚠️ No place found for query:', query, 'Status:', status);
          resolve(null);
        }
      });
    });
  } catch (error) {
    console.error('❌ Error searching for place:', error);
    return null;
  }
};

/**
 * Get photo URL from a Places photo object
 * @param {Object} photo - Google Maps photo object
 * @param {number} maxWidth - Maximum width for the photo (default: 1200)
 * @returns {string} Full photo URL
 */
export const getPlacePhotoUrl = (photo, maxWidth = 1200) => {
  if (!photo || !photo?.getUrl) {
    return null;
  }

  try {
    return photo?.getUrl({ maxWidth: maxWidth });
  } catch (error) {
    console.error('❌ Error getting photo URL:', error);
    return null;
  }
};

/**
 * Get the best scenic photo for a state park
 * @param {string} parkName - Name of the state park
 * @param {Object} location - Optional location {lat, lng}
 * @returns {Promise<Object>} Photo data {url, photoReference, attribution}
 */
export const getStateParkPhoto = async (parkName, location = null) => {
  try {
    // Search for the park
    const place = await searchPlace(parkName, location);

    if (!place || !place?.photos || place?.photos?.length === 0) {
      console.warn('⚠️ No photos found for park:', parkName);
      return getFallbackParkImage(parkName);
    }

    // Get the first photo (usually the best/featured one)
    const photo = place?.photos?.[0];
    const photoUrl = getPlacePhotoUrl(photo, 1200);

    if (!photoUrl) {
      console.warn('⚠️ Could not generate photo URL for:', parkName);
      return getFallbackParkImage(parkName);
    }

    return {
      url: photoUrl,
      photoReference: null, // Not available in browser API
      attribution: photo?.html_attributions?.[0] || '',
      width: photo?.width || 1200,
      height: photo?.height || 800,
      placeId: place?.place_id,
      placeName: place?.name,
      alt: `Scenic view of ${place?.name || parkName} - Beautiful landscape photography showing natural features and outdoor recreation opportunities`
    };
  } catch (error) {
    console.error('❌ Error fetching state park photo:', error);
    return getFallbackParkImage(parkName);
  }
};

/**
 * Get multiple photos for a state park (for galleries)
 * @param {string} parkName - Name of the state park
 * @param {number} maxPhotos - Maximum number of photos to fetch (default: 5)
 * @param {Object} location - Optional location {lat, lng}
 * @returns {Promise<Array>} Array of photo objects
 */
export const getStateParkPhotos = async (parkName, maxPhotos = 5, location = null) => {
  try {
    const place = await searchPlace(parkName, location);

    if (!place || !place?.photos || place?.photos?.length === 0) {
      console.warn('⚠️ No photos found for park:', parkName);
      return [getFallbackParkImage(parkName)];
    }

    // Get multiple photos
    const photos = place?.photos?.slice(0, maxPhotos)?.map((photo, index) => {
      const photoUrl = getPlacePhotoUrl(photo, 1200);
      
      if (!photoUrl) {
        return null;
      }

      return {
        url: photoUrl,
        photoReference: null,
        attribution: photo?.html_attributions?.[0] || '',
        width: photo?.width || 1200,
        height: photo?.height || 800,
        placeId: place?.place_id,
        placeName: place?.name,
        alt: `${place?.name || parkName} - View ${index + 1}: Scenic landscape showcasing natural beauty and outdoor recreation areas`,
        isPrimary: index === 0
      };
    })?.filter(Boolean); // Remove any null entries

    return photos?.length > 0 ? photos : [getFallbackParkImage(parkName)];
  } catch (error) {
    console.error('❌ Error fetching state park photos:', error);
    return [getFallbackParkImage(parkName)];
  }
};

/**
 * Batch fetch photos for multiple parks (for trip itineraries)
 * @param {Array<string>} parkNames - Array of park names
 * @param {Object} options - Options {location, maxPhotos}
 * @returns {Promise<Object>} Object mapping park names to photo data
 */
export const getMultipleParkPhotos = async (parkNames, options = {}) => {
  try {
    const { location = null, maxPhotos = 1 } = options;
    
    // Process parks sequentially to avoid rate limiting
    const photoMap = {};
    
    for (const parkName of parkNames) {
      try {
        const photoData = maxPhotos === 1 
          ? await getStateParkPhoto(parkName, location)
          : await getStateParkPhotos(parkName, maxPhotos, location);
        
        photoMap[parkName] = photoData;
        
        // Small delay to avoid hitting rate limits
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.warn('⚠️ Failed to fetch photos for:', parkName, error);
        photoMap[parkName] = getFallbackParkImage(parkName);
      }
    }

    return photoMap;
  } catch (error) {
    console.error('❌ Error in batch photo fetch:', error);
    return {};
  }
};

/**
 * Get fallback image when Google Maps API is unavailable or fails
 * @param {string} parkName - Name of the park
 * @returns {Object} Fallback image data
 */
export const getFallbackParkImage = (parkName) => {
  // Generate a deterministic but varied fallback based on park name
  const hash = parkName?.split('')?.reduce((acc, char) => {
    return char?.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  
  // Use hash to select from curated Unsplash nature/park images
  // ✅ CRITICAL FIX: Added 'photo-' prefix to all Unsplash image IDs
  const fallbackImages = [
    'photo-1506905925346-21bda4d32df4', // Mountain landscape
    'photo-1441974231531-c6227db76b6e', // Forest trail
    'photo-1472214103451-9374bd1c798e', // Misty mountains
    'photo-1511593358241-7eea1f3c84e5', // Desert landscape
    'photo-1469854523086-cc02fe5d8800', // Sunset over water
    'photo-1464822759023-fed622ff2c3b', // Mountain lake
    'photo-1501594907352-04cda38ebc29', // Coastal cliffs
    'photo-1426604966848-d7adac402bff', // Alpine meadow
    'photo-1447752875215-b2761acb3c5d', // Rolling hills
    'photo-1500964757637-c85e8a162699', // Waterfall
    'photo-1507003211169-0a1dd7228f2d', // Canyon vista
    'photo-1490730141103-6cac27aaab94', // Valley landscape
    'photo-1505142468610-359e7d316be0', // Rock formations
    'photo-1488554378835-f7acf46e6c98', // Prairie grassland
    'photo-1418065460487-3e41a6c84dc5', // Lake reflection
  ];
  
  // Select image based on hash (deterministic for same park name)
  const imageIndex = Math?.abs(hash) % fallbackImages?.length;
  const selectedImage = fallbackImages?.[imageIndex];
  
  const fallbackUrl = `https://images.unsplash.com/${selectedImage}?auto=format&fit=crop&w=1200&q=80`;
  
  return {
    url: fallbackUrl,
    photoReference: null,
    attribution: 'Photo from Unsplash',
    width: 1200,
    height: 800,
    placeId: null,
    placeName: parkName,
    alt: `Beautiful scenic landscape representing ${parkName} with natural outdoor recreation areas and scenic views`,
    isFallback: true
  };
};

/**
 * Validate if Google Maps API key is configured
 * @returns {boolean}
 */
export const isGoogleMapsConfigured = () => {
  return isValidApiKey();
};

/**
 * Extract park names from trip itinerary
 * @param {Array} itinerary - Trip itinerary data
 * @returns {Array<string>} Unique park names
 */
export const extractParkNamesFromItinerary = (itinerary) => {
  const parkNames = new Set();
  
  itinerary?.forEach(day => {
    // Extract from day location
    if (day?.location) {
      parkNames?.add(day?.location);
    }
    
    // Extract from activities
    day?.activities?.forEach(activity => {
      if (activity?.location) {
        parkNames?.add(activity?.location);
      }
    });
  });
  
  return Array.from(parkNames);
};

/**
 * Get detailed place information including state/administrative area and city
 * @param {string} placeId - Google Place ID
 * @returns {Promise<Object>} Place details including state and city information
 */
export const getPlaceDetails = async (placeId) => {
  try {
    if (!isValidApiKey()) {
      console.warn('⚠️ Google Maps API key not configured. Cannot fetch place details.');
      return null;
    }

    // Load Google Maps API
    await loadGoogleMapsScript();

    // Create a temporary map element (required for PlacesService)
    const mapDiv = document.createElement('div');
    const map = new window.google.maps.Map(mapDiv);

    // Create PlacesService instance
    const service = new window.google.maps.places.PlacesService(map);

    // Request place details with address components
    const request = {
      placeId: placeId,
      fields: [
        'place_id',
        'name',
        'formatted_address',
        'address_components',
        'geometry',
        'photos',
        'types'
      ]
    };

    return new Promise((resolve, reject) => {
      service.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          // Extract state and city from address components
          let state = null;
          let stateShort = null;
          let city = null;
          
          place?.address_components?.forEach(component => {
            if (component?.types?.includes('administrative_area_level_1')) {
              state = component?.long_name; // Full state name (e.g., "California")
              stateShort = component?.short_name; // State abbreviation (e.g., "CA")
            }
            // Extract city from multiple possible component types
            if (component?.types?.includes('locality') || 
                component?.types?.includes('sublocality') ||
                component?.types?.includes('administrative_area_level_2')) {
              // Prioritize locality over administrative_area_level_2
              if (!city || component?.types?.includes('locality')) {
                city = component?.long_name;
              }
            }
          });

          const result = {
            placeId: place?.place_id,
            name: place?.name,
            formattedAddress: place?.formatted_address,
            state: state,
            stateShort: stateShort,
            city: city, // ✅ NEW: Added city extraction
            geometry: place?.geometry ? {
              lat: place?.geometry?.location?.lat(),
              lng: place?.geometry?.location?.lng()
            } : null,
            photos: place?.photos || [],
            types: place?.types || []
          };

          console.log('✅ Fetched place details with city and state:', result);
          resolve(result);
        } else {
          console.warn('⚠️ Could not fetch place details for Place ID:', placeId, 'Status:', status);
          resolve(null);
        }
      });
    });
  } catch (error) {
    console.error('❌ Error fetching place details:', error);
    return null;
  }
};

/**
 * Search for a place and return both place data and place_id
 * Enhanced version that includes place_id, state, and city information
 * @param {string} query - The search query (e.g., "Yellowstone State Park")
 * @param {Object} location - Optional location bias {lat, lng}
 * @returns {Promise<Object>} Place details with place_id, state, and city information
 */
export const searchPlaceWithDetails = async (query, location = null) => {
  try {
    // First search for the place
    const place = await searchPlace(query, location);
    
    if (!place || !place?.place_id) {
      return null;
    }

    // Then get detailed information including state and city
    const details = await getPlaceDetails(place?.place_id);
    
    return {
      ...details,
      photos: place?.photos || [] // Include photos from search result
    };
  } catch (error) {
    console.error('❌ Error in searchPlaceWithDetails:', error);
    return null;
  }
};

export default {
  searchPlace,
  getPlacePhotoUrl,
  getStateParkPhoto,
  getStateParkPhotos,
  getMultipleParkPhotos,
  getFallbackParkImage,
  isGoogleMapsConfigured,
  extractParkNamesFromItinerary,
  getPlaceDetails,
  searchPlaceWithDetails
};